/*
 * User Susceptibility Card.
 *
 * Click rate broken out by lure tag. Sits on UserDetailPage (email prop)
 * and MetaDetailPage (events prop: the slice's SEND_LOG subset) and
 * answers "which psychological levers actually land on this person/group?".
 * Mirrors the visual language of BarSegmentCard: 24px bars, primary-colour
 * fill, value rendered to the right of the row.
 *
 * Calculations: tagAnalytics.tagsForUser(email) — or clickRateByTag(events)
 * when an event list is supplied — returns
 *   [{ tag, label, sends, clicks, rate }, ...]
 * Tenancy baseline rate comes from clickRateByTag(SEND_LOG) keyed by tag.
 *
 * Postgres mapping: replace tagsForUser with
 *   `tagApi.useUserTagBreakdownQuery(email)` -> joined view over
 *   `simulations_sent` + `simulation_tags`. Cache 1h.
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from 'themes/ThemeContext';
import { motion } from 'motion/react';
import MainCard from '../../../components/cards/MainCard';
import CountUp from 'react-countup';
import { SEND_LOG, DATA_EPOCH } from '../data';
import { logInPeriod } from '../hooks/useDashboardData';
import { tagsForUser, clickRateByTag, TAG_DESCRIPTIONS } from '../tagAnalytics';

const fadeUp = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };

// Tenancy-wide baseline over the same period as the user's own rates (BD-1003),
// cached per data epoch + window so it follows live data and the date filter.
let baselineCache = null;
let baselineKey = null;
export function baseline(range = {}) {
    const key = `${DATA_EPOCH}|${range.period || ''}|${range.startDate || ''}|${range.endDate || ''}`;
    if (baselineKey !== key) {
        baselineCache = {};
        for (const row of clickRateByTag(logInPeriod(SEND_LOG, range))) baselineCache[row.tag] = row.rate;
        baselineKey = key;
    }
    return baselineCache;
}

function rateColor(rate) {
    if (rate >= 50) return 'var(--red)';
    if (rate >= 25) return 'var(--amber)';
    if (rate > 0)   return 'var(--text)';
    return 'var(--green)';
}

function ShimmerBar({ color, opacity = 1, pct, delay, isDark, baselinePct }) {
    const [value, setValue] = useState(0);

    useEffect(() => {
        setValue(0);
        const t = setTimeout(() => setValue(pct), 150 + delay * 1000);
        return () => clearTimeout(t);
    }, [pct, delay]);

    return (
        <Box sx={{ position: 'relative' }}>
            <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'grey.100',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 1,
                        background: color,
                        opacity,
                        transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
                        transitionDelay: `${delay}s`,
                    },
                }}
            />
            {baselinePct != null && baselinePct > 0 && (
                <Tooltip title={`Tenancy average: ${baselinePct.toFixed(1)}%`} placement="top" arrow>
                    {/* Wide transparent hitbox centred on the baseline so the
                        tooltip is easy to trigger; the dashed line sits inside. */}
                    <Box sx={{
                        position: 'absolute',
                        top: -6,
                        bottom: -6,
                        left: `${baselinePct}%`,
                        width: 16,
                        transform: 'translateX(-8px)',
                        display: 'flex',
                        justifyContent: 'center',
                        pointerEvents: 'auto',
                        cursor: 'default',
                    }}>
                        <Box sx={{
                            width: 0,
                            height: '100%',
                            borderLeft: '3px dashed',
                            borderColor: isDark ? 'rgba(255,255,255,0.92)' : 'rgba(17,20,24,0.9)',
                        }} />
                    </Box>
                </Tooltip>
            )}
        </Box>
    );
}

function PageButton({ disabled, onClick, label, children }) {
    return (
        <Box
            component="button"
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            sx={{
                width: 24, height: 24,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                p: 0, fontSize: 14, lineHeight: 1,
                border: '1px solid', borderColor: 'divider', borderRadius: '6px',
                bgcolor: 'background.paper', color: 'text.secondary',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                transition: 'border-color .15s, color .15s, background-color .15s',
                '&:hover': disabled ? undefined : { borderColor: 'secondary.main', color: 'secondary.main' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'secondary.main', outlineOffset: 2 },
            }}
        >
            {children}
        </Box>
    );
}

const PAGE_SIZE = 5;

export default function UserSusceptibilityCard({ email, events, tagRows, baselineRows, period, startDate, endDate }) {
    const theme = useTheme();
    const { themeMode } = useThemeContext();
    const isDark = themeMode === 'dark';
    const [page, setPage] = useState(0);

    const baselineRates = Array.isArray(baselineRows)
        ? Object.fromEntries(baselineRows.map((r) => [r.tag, r.rate]))
        : baseline({ period, startDate, endDate });
    const rows = (Array.isArray(tagRows) ? tagRows : events ? clickRateByTag(events) : tagsForUser(email))
        .filter((r) => r.sends > 0)
        .sort((a, b) => b.rate - a.rate || b.sends - a.sends);

    if (rows.length === 0) {
        return (
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ p: 3 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: 0.5 }}>
                            Susceptibility profile
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            No tagged simulations sent yet.
                        </Typography>
                    </Box>
                </MainCard>
            </motion.div>
        );
    }

    const max = Math.max(100, ...rows.map((r) => r.rate));
    const totalSends = rows.reduce((a, r) => a + r.sends, 0);

    const totalPages = Math.ceil(rows.length / PAGE_SIZE);
    const safePage = Math.min(page, totalPages - 1);
    const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    return (
        <motion.div variants={fadeUp} initial="hidden" animate="visible" style={{ height: '100%' }}>
            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Susceptibility by lure type
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                Click rate per psychological lure, ranked highest first. Dashed line shows the tenancy average.
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 2 }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled' }}>
                                Sample
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                                {totalSends} sends
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {pageRows.map((r, i) => {
                            const widthPct = (r.rate / max) * 100;
                            const base = baselineRates[r.tag] || 0;
                            const baselinePct = base / max * 100;
                            const color = rateColor(r.rate);
                            const delta = r.rate - base;
                            return (
                                <Tooltip
                                    key={r.tag}
                                    title={TAG_DESCRIPTIONS[r.tag] || r.label}
                                    placement="left"
                                    arrow
                                >
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>
                                                    {r.label}
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                                    {r.clicks} of {r.sends} clicked
                                                </Typography>
                                                {Math.abs(delta) >= 5 && (
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center',
                                                        fontSize: 10, fontWeight: 700,
                                                        px: '6px', py: '1px',
                                                        borderRadius: '3px',
                                                        bgcolor: delta > 0 ? 'var(--red-bg)' : 'var(--green-bg)',
                                                        color: delta > 0 ? 'var(--red-text)' : 'var(--green-text)',
                                                    }}>
                                                        {delta > 0 ? '+' : ''}{Math.round(delta)} vs org
                                                    </Box>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color, letterSpacing: '-0.01em' }}>
                                                <CountUp end={r.rate} duration={1.2} decimals={r.rate < 10 ? 1 : 0} />%
                                            </Typography>
                                        </Box>
                                        <ShimmerBar
                                            color={color}
                                            opacity={1 - (i / pageRows.length) * 0.25}
                                            pct={widthPct}
                                            delay={i * 0.1}
                                            isDark={isDark}
                                            baselinePct={baselinePct}
                                        />
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                {safePage * PAGE_SIZE + 1}-{Math.min((safePage + 1) * PAGE_SIZE, rows.length)} of {rows.length} lures
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <PageButton disabled={safePage === 0} onClick={() => setPage(safePage - 1)} label="Previous page">‹</PageButton>
                                <PageButton disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)} label="Next page">›</PageButton>
                            </Box>
                        </Box>
                    )}
                </Box>
            </MainCard>
        </motion.div>
    );
}
